/* External */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import isEmpty from 'lodash-es/isEmpty';

/* Ott-common */
import InteractiveElement from '@dtvgo/interactive-element';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Components */
import MiniScheduleCardSkeleton from './MiniScheduleCardSkeleton';
import InteractiveIcon from '../../../../assets/svg/interactive-icon.svg?react';

/* Utils */
import { Content, Channel } from '../../../../storage/models';
import { getImage } from '../../../../utils/imageUtils';
import withInteractiveFocusable from '../../../../hocs/withInteractiveFocusable.hoc';
import {
  getSchedule,
  useTitle,
  isInteractivityAllowed,
} from '../../../../utils/metaDataUtils';
import useContent from '../../../../hooks/useContent';
import parentalControlBlock from '../../../../utils/parentalControlUtils';
import { useUserAccount } from '../../../../providers/UserProvider';

/* Styles */
import './MiniScheduleCard.scss';

const CHANNEL_IMAGE_WIDTH = 80;

function MiniScheduleCard({
  className,
  focused,
  onMouseDown,
  onMouseEnter,
  schedule,
  channel,
  isFetching,
  currentChannelId,
}) {
  const {
    startTime, endTime, blackout, content: contentId,
  } = schedule || {};
  const interactiveIcon = getJsonImage('interactive-icon');

  const content = useContent(contentId);

  const isContentBlockedByParentalControl = parentalControlBlock(content);

  const [isCurrentChannel, setIsCurrentChannel] = useState(false);

  const { timeZone } = useIntl();

  const ref = useRef(undefined);

  const { images } = channel || {};

  const imageUrl = useMemo(() => getImage(
    images,
    CHANNEL_IMAGE_WIDTH,
  ), [images]);

  const title = useTitle(content, schedule);

  const { uid: userId, iso2Code } = useUserAccount() || {};
  const isInteractive = isInteractivityAllowed(schedule, iso2Code, userId);

  const isThereTitleOrSchedules = useMemo(
    () => !!(title && startTime && endTime) && schedule && !isEmpty(schedule),
    [endTime, schedule, startTime, title],
  );

  const scheduleContent = useMemo(() => {
    if (isFetching) {
      return undefined;
    }

    return (schedule && isThereTitleOrSchedules) && (
    <span className="dtv-html5-c-mini-schedule-card__schedule">
      {getSchedule(schedule, timeZone)}
    </span>
    );
  }, [isFetching, isThereTitleOrSchedules, schedule, timeZone]);

  const metadata = useMemo(() => {
    if (isThereTitleOrSchedules) {
      return (
        <>
          <div className="dtv-html5-c-mini-schedule-card__title-container">
            <h4 className={`dtv-html5-c-mini-schedule-card__title
              ${isCurrentChannel ? 'dtv-html5-c-mini-schedule-card__title--live-tag' : ''}
              ${blackout ? 'dtv-html5-c-mini-schedule-card__title--blackout' : ''}`}
            >
              {title}
            </h4>
            {isInteractive && (
              <span className="dtv-html5-c-mini-schedule-card__title-container-icon">
                {interactiveIcon ? (
                  <img src={interactiveIcon} alt="interactive icon" />
                ) : (
                  <InteractiveIcon />
                )}
              </span>
            )}
          </div>
          {(isContentBlockedByParentalControl && !blackout) && (
            <div className="dtv-html5-c-mini-schedule-card__parental-control">
              <span className="dtv-html5-c-mini-schedule-card__parental-control-text">
                <FormattedMessage
                  id="scheduleMiniEpg.parentalControl.message"
                  defaultMessage="Bloqueado por límite de edad"
                />
              </span>
            </div>
          )}
          <div className="dtv-html5-c-mini-schedule-card__schedule-container">
            {blackout ? (
              <span className="dtv-html5-c-mini-schedule-card__blackout-text">
                <FormattedMessage
                  id="live.blackout.short"
                  defaultMessage="No disponible en su área"
                />
              </span>
            ) : scheduleContent}
          </div>
        </>
      );
    }
    return (
      <span className="dtv-html5-c-mini-schedule-card__missing-metadata">
        <FormattedMessage
          id="scheduleMiniEpg.missing.metadata"
          defaultMessage="Sin programación disponible."
        />
      </span>
    );
  }, [
    blackout,
    isContentBlockedByParentalControl,
    isCurrentChannel,
    isThereTitleOrSchedules,
    scheduleContent,
    title,
    isInteractive,
    interactiveIcon,
  ]);

  useEffect(() => {
    if (currentChannelId && currentChannelId === channel?.channelId) {
      setIsCurrentChannel(true);
    } else {
      setIsCurrentChannel(false);
    }
  }, [currentChannelId, channel]);

  return (
    isFetching ? <MiniScheduleCardSkeleton /> : (
      <InteractiveElement
        className={`dtv-html5-c-mini-schedule-card ${
          focused ? 'dtv-html5-c-mini-schedule-card--focused' : ''
        } ${
          blackout || !isThereTitleOrSchedules ? 'dtv-html5-c-mini-schedule-card--inactive' : ''
        } ${
          isFetching ? 'dtv-html5-c-mini-schedule-card--loading' : ''
        } ${className}`}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        ref={ref}
      >
        <div className="dtv-html5-c-mini-schedule-card__info-schedule">
          <img
            className="dtv-html5-c-mini-schedule-card__logo"
            src={imageUrl}
            alt={channel?.title || ''}
          />
          <div className="dtv-html5-c-mini-schedule-card__info">
            {isCurrentChannel && (
              <p className="dtv-html5-c-mini-schedule-card__watching">
                <FormattedMessage id="miniEpgScheduleCardWatching.title" />
              </p>
            )}
            {metadata}
          </div>
        </div>
      </InteractiveElement>
    )
  );
}

MiniScheduleCard.propTypes = {
  className: PropTypes.string,
  focused: PropTypes.bool.isRequired,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
  isFetching: PropTypes.bool,
  schedule: PropTypes.instanceOf(Content),
  channel: PropTypes.instanceOf(Channel),
  currentChannelId: PropTypes.string,
};

MiniScheduleCard.defaultProps = {
  className: '',
  onMouseDown: undefined,
  onMouseEnter: undefined,
  isFetching: false,
  schedule: undefined,
  channel: undefined,
  currentChannelId: '',
};

export default memo(withInteractiveFocusable()(memo(MiniScheduleCard)));
